// Keeps the master key so a page reload does not ask for the password again.
//
// Two tiers:
//   sessionStorage  – survives F5, dies with the tab (used when "记住这台设备" is off)
//   IndexedDB       – survives a browser restart, wrapped with a non-extractable AES-GCM
//                     key that also lives in IndexedDB, so the raw key bytes never exist in JS
//
// Trade-off: anyone who can run script on this origin (XSS, malicious extension) can decrypt
// with a remembered key just as the restored session could. "登出" wipes both tiers.

const DB_NAME = 'pes-unlock';
const DB_VERSION = 1;
const STORE = 'wrap';
const SESSION_KEY = 'pes.unlock';
const RECORD_KEY = 'unlock';
const WRAP_KEY = 'wrap';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const hasIdb = () => typeof indexedDB !== 'undefined' && typeof crypto !== 'undefined' && !!crypto.subtle;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function run(db, mode, work) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    let result;
    const req = work(t.objectStore(STORE));
    if (req) req.onsuccess = () => (result = req.result);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

async function wrapKey(db, create) {
  const existing = await run(db, 'readonly', (store) => store.get(WRAP_KEY));
  if (existing || !create) return existing ?? null;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await run(db, 'readwrite', (store) => store.put(key, WRAP_KEY));
  return key;
}

export function saveSession(record) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(record));
  } catch {
    /* storage disabled */
  }
}

export function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export async function savePersistent(record) {
  if (!hasIdb()) return false;
  try {
    const db = await openDb();
    const key = await wrapKey(db, true);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plain = new TextEncoder().encode(JSON.stringify(record));
    const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain);
    await run(db, 'readwrite', (store) =>
      store.put({ iv, cipher, expiresAt: Date.now() + TTL_MS }, RECORD_KEY),
    );
    return true;
  } catch {
    return false;
  }
}

export async function readPersistent() {
  if (!hasIdb()) return null;
  try {
    const db = await openDb();
    const rec = await run(db, 'readonly', (store) => store.get(RECORD_KEY));
    if (!rec) return null;
    if (!rec.expiresAt || rec.expiresAt < Date.now()) {
      await run(db, 'readwrite', (store) => store.delete(RECORD_KEY));
      return null;
    }
    const key = await wrapKey(db, false);
    if (!key) return null;
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: rec.iv }, key, rec.cipher);
    return JSON.parse(new TextDecoder().decode(plain));
  } catch {
    return null;
  }
}

/** Newest record available: the tab's own first, then the persistent one. */
export async function restore() {
  return readSession() ?? (await readPersistent());
}

export async function clearAll() {
  clearSession();
  if (!hasIdb()) return;
  try {
    const db = await openDb();
    await run(db, 'readwrite', (store) => {
      store.delete(RECORD_KEY);
      store.delete(WRAP_KEY);
    });
  } catch {
    /* ignore */
  }
}
